import axios from "axios";
import Joi from "joi";
import { has, isEmpty, merge, omit, pick } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { ButtonDefault } from "../components/Buttons";
import AppContext from "../Context/AppContext";
import { leadIdGenerator } from "../utils/leadIdGenerator";
import { validateForm, validateProperty } from "../utils/validation";
import AppointmentPicker from "./form/AppointmentPicker";
import CheckboxGroup from "./form/CheckboxGroup";
import InputField from "./form/InputField";
import SingleCheckbox from "./form/SingleCheckbox";

function FormContact({ setFormSubmitted }) {
  // AMENDED [2026-06-09]: Added isNewBuild to context destructure.
  // Previously: const { referral } = useContext(AppContext);
  // AMENDED [10/07/2026]: Added appointmentType to context destructure - needed
  // to branch protection-review submit behaviour (admin email list, DB skip).
  // Previously: const { referral, isNewBuild } = useContext(AppContext);
  const { referral, isNewBuild, appointmentType } = useContext(AppContext);

  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    development: "",
    appointmentDay: null,
    appointmentGroup: null,
    appointmentTime: null,
    appointmentCallSoon: 0,
    storeData: 0,
    contactMethods: {},
    referral: null,
  });
  const [formDataChange, setFormDataChange] = useState({});
  const [appointment, setAppointment] = useState({});
  const [errors, setErrors] = useState({});

  // schemaObj in useMemo: stable reference safe to use in useEffect dep array.
  const schemaObj = useMemo(
    () => ({
      firstName: Joi.string().required().messages({
        "string.empty": "Please enter your first name",
      }),
      lastName: Joi.string().required().messages({
        "string.empty": "Please enter your last name",
      }),
      email: Joi.string().email({ tlds: false }).required().messages({
        "string.empty": "Please enter your email address",
        "string.email": "Please enter a valid email address",
      }),
      phone: Joi.any(),
      development: Joi.any(),
      appointmentDay: Joi.any(),
      appointmentGroup: Joi.any(),
      appointmentTime: Joi.any(),
      appointmentCallSoon: Joi.any(),
      storeData: Joi.any(),
      contactMethods: Joi.any(),
      referral: Joi.any(),
    }),
    [],
  ); // FIX: empty deps — schema never changes

  // FIX [eslint react-hooks/exhaustive-deps Line 37]: Used functional update to avoid
  // needing 'data' in the dependency array.
  // Previously: setData({ ...data, referral: referral ? referral : null });
  //             }, [referral]);
  useEffect(() => {
    setData((prev) => ({ ...prev, referral: referral ? referral : null })); // FIX: functional update
  }, [referral]);

  // FIX: 'errors' removed from deps — validateProperty uses functional setErrors(prev => ...)
  // internally, so errors never needs to be in the dep array (avoids infinite loop).
  useEffect(() => {
    if (!isEmpty(formDataChange))
      validateProperty(formDataChange, schemaObj, setErrors);
  }, [formDataChange, schemaObj]);

  const handleChange = ({ currentTarget }) => {
    const input = {
      name: currentTarget.name,
      value: currentTarget.value,
    };
    doAfterChange(input);
  };

  // const handleSelectChange = ({ currentTarget }) => {
  //   const input = {
  //     name: currentTarget.name,
  //     value: parseInt(currentTarget.value),
  //   };
  //   doAfterChange(input);
  // };

  const handleCheckboxChange = ({ currentTarget }) => {
    const input = {
      name: currentTarget.name,
      value: parseInt(currentTarget.value) === 0 ? 1 : 0,
    };
    doAfterChange(input);
  };

  const handleCheckboxGroupChange = ({ currentTarget }) => {
    const input = {
      name: currentTarget.name,
      value: parseInt(currentTarget.value) === 0 ? 1 : 0,
      key: currentTarget.getAttribute("data-key"),
    };

    // FIX: won't fit into doAfterChange(input) template
    // setData({
    //   ...data,
    //   [input.name]: { ...data[input.name], [input.key]: input.value },
    // });

    doAfterChange({
      name: input.name,
      value: { ...data[input.name], [input.key]: input.value },
    });
  };

  const doAfterChange = (input) => {
    setData({ ...data, [input.name]: input.value });
    setFormDataChange(input);
  };

  const handleSubmit = async () => {
    document.getElementById("contact").scrollIntoView();
    const reference = leadIdGenerator();
    //data.leadId = leadId;
    data.reference = reference;

    data.leadType = "Appointment";

    data.appointment = appointment;
    //data.appointmentText = appointment.text; // DELETE after cascade update.

    // ADDED [10/07/2026]: Flag used to branch protection-review-specific
    // submit behaviour (admin recipient list, DB entry skip) below.
    const isProtectionReview = appointmentType === "protection-review";

    // AMENDED [10/07/2026]: Protection review appointments notify an additional
    // admin recipient list (referrals@ and Jamie Richmond) in prod, alongside
    // the existing gmail address. Dev fallback unchanged. All other routes
    // keep the original behaviour.
    // Previously:
    // const emailToAdmin =
    //   process.env.NODE_ENV === "production"
    //     ? [{ email: "threshold.mortgages@gmail.com", name: "New Enquiry" }]
    //     : [{ email: "rich.ray@hotmail.co.uk", name: "Richard" }];
    // AMENDED [10/07/2026]: Added three more prod recipients to the protection
    // review admin list (Jav Uddin, Rashida Howard, Aidan Nicholls).
    // Previously:
    // const emailToAdmin = isProtectionReview
    //   ? process.env.NODE_ENV === "production"
    //     ? [
    //         { email: "referrals@thresholdmortgages.com", name: "Referrals" },
    //         {
    //           email: "jamie.richmond@thresholdmortgages.com",
    //           name: "Jamie Richmond",
    //         },
    //         { email: "threshold.mortgages@gmail.com", name: "New Enquiry" },
    //       ]
    //     : [{ email: "rich.ray@hotmail.co.uk", name: "Richard" }]
    //   : process.env.NODE_ENV === "production"
    //   ? [{ email: "threshold.mortgages@gmail.com", name: "New Enquiry" }]
    //   : [{ email: "rich.ray@hotmail.co.uk", name: "Richard" }];
    const emailToAdmin = isProtectionReview
      ? process.env.NODE_ENV === "production"
        ? [
            { email: "referrals@thresholdmortgages.com", name: "Referrals" },
            {
              email: "jamie.richmond@thresholdmortgages.com",
              name: "Jamie Richmond",
            },
            // AMENDED [10/07/2026]: gmail recipient name "New Enquiry" -> "Admin".
            // Previously: { email: "threshold.mortgages@gmail.com", name: "New Enquiry" },
            { email: "threshold.mortgages@gmail.com", name: "Admin" },
            { email: "jav.uddin@thresholdmortgages.com", name: "Jav Uddin" },
            {
              email: "rashida.howard@thresholdmortgages.com",
              name: "Rashida Howard",
            },
            {
              email: "aidan.nicholls@thresholdmortgages.com",
              name: "Aidan Nicholls",
            },
          ]
        : [{ email: "rich.ray@hotmail.co.uk", name: "Richard" }]
      : // AMENDED [10/07/2026]: gmail recipient name "New Enquiry" -> "Admin".
      // Previously:
      // : process.env.NODE_ENV === "production"
      // ? [{ email: "threshold.mortgages@gmail.com", name: "New Enquiry" }]
      // : [{ email: "rich.ray@hotmail.co.uk", name: "Richard" }];
      process.env.NODE_ENV === "production"
      ? [{ email: "threshold.mortgages@gmail.com", name: "Admin" }]
      : [{ email: "rich.ray@hotmail.co.uk", name: "Richard" }];

    // AMENDED [20/08/2026]: Added Protection Review label to email subject
    // ternary, checked ahead of the referral branches so a protection-review
    // appointment's subject line reflects that instead of falling through to
    // "(General)". Uses isProtectionReview, already defined above (line 142).
    // Previously:
    // // ADDED 08/06/2025: Added Alan Hawkins referral label to email subject ternary.
    // // Previously:
    // // const emailSubjectAdmin = `Contact Form App / Book an Appointment Form ${
    // //   referral === "t"
    // //     ? "(Taylor Wimpey)"
    // //     : referral === "p"
    // //     ? "(Persimmon)"
    // //     : "(General)"
    // // } / ${data.firstName} ${data.lastName}`;
    // const emailSubjectAdmin = `Contact Form App / Book an Appointment Form ${
    //   referral === "t"
    //     ? "(Taylor Wimpey)"
    //     : referral === "p"
    //     ? "(Persimmon)"
    //     : referral === "ah"
    //     ? "(Alan Hawkins)"
    //     : "(General)"
    // } / ${data.firstName} ${data.lastName}`;
    const emailSubjectAdmin = `Contact Form App / Book an Appointment Form ${
      isProtectionReview
        ? "(Protection Review)"
        : referral === "t"
        ? "(Taylor Wimpey)"
        : referral === "p"
        ? "(Persimmon)"
        : referral === "ah"
        ? "(Alan Hawkins)"
        : "(General)"
    } / ${data.firstName} ${data.lastName}`;

    const emailToClient = [
      {
        email: data.email,
        name: `${data.firstName} ${data.lastName}`,
      },
    ];
    const emailSubjectClient =
      "Your appointment with Threshold Mortgages is confirmed";

    let emailParams;
    emailParams = pick(data, [
      "firstName",
      "lastName",
      "email",
      "phone",
      "development",
      "storeData",
    ]);
    emailParams.reference = reference;
    emailParams.leadType = data.leadType;
    // ADDED 08/06/2025: Added Alan Hawkins referral value to emailParams.referral ternary.
    // Previously:
    // emailParams.referral =
    //   referral === "t"
    //     ? "Taylor Wimpey"
    //     : referral === "p"
    //     ? "Persimmon"
    //     : null;
    emailParams.referral =
      referral === "t"
        ? "Taylor Wimpey"
        : referral === "p"
        ? "Persimmon"
        : referral === "ah"
        ? "Alan Hawkins"
        : null;

    emailParams.appointment = appointment;
    emailParams.storeData = data.storeData ? "Yes Please" : "No Thanks";

    const officePhone = "03300 249 115";
    emailParams.officePhone = officePhone;

    const formatPhone = (phoneNumber) => {
      const formattedPhone = phoneNumber.substring(1).replace(/\s/g, "");
      const prefixedPhone = `+44${formattedPhone}`;
      return prefixedPhone;
    };

    const getSmsMsg = () => {
      const appointmentMessage =
        data.appointmentDay !== null && data.appointmentTime !== null
          ? `We have booked you an appointment for:\n\n${appointment.text}\n\nAn adviser will call you by phone to discuss your requirements.\n\n`
          : "An adviser will call you by phone as soon as possible to discuss your requirements.\n\n";

      return `Thank you for your interest in Threshold Mortgages.\n\n${appointmentMessage}For all enquiries call ${officePhone}.`;
    };

    const filteredData = omit(data, [
      "appointmentDay",
      "appointmentGroup",
      "appointmentTime",
      "appointmentCallSoon",
      "contactMethods",
    ]);

    // ADDED 08/06/2025: Added Alan Hawkins DB endpoint to ternary.
    // Previously:
    // const DbEndpoint =
    //   referral === "t"
    //     ? "/api/tw-leads"
    //     : referral === "p"
    //     ? "/api/ph-leads"
    //     : "/api/gen-leads";
    const DbEndpoint =
      referral === "t"
        ? "/api/tw-leads"
        : referral === "p"
        ? "/api/ph-leads"
        : referral === "ah"
        ? "/api/ah-leads"
        : "/api/gen-leads";

    const [
      emailToClientReport,
      emailToAdminReport,
      smsToClientReport,
      sendToDatabaseReport,
    ] = await Promise.allSettled([
      axios.post(process.env.REACT_APP_API_URL + "/api/send-email", {
        to: emailToClient,
        subject: emailSubjectClient,
        templateId: 225,
        params: emailParams,
      }),
      axios.post(process.env.REACT_APP_API_URL + "/api/send-email", {
        to: emailToAdmin,
        subject: emailSubjectAdmin,
        templateId: 226,
        params: emailParams,
      }),
      axios.post(process.env.REACT_APP_API_URL + "/api/send-sms", {
        recipient: formatPhone(data.phone),
        content: getSmsMsg(),
      }),
      // AMENDED [10/07/2026]: Protection review appointments don't get a DB
      // entry - substitute a resolved no-op so the Promise.allSettled array
      // indices (and the destructure below) stay unchanged for every route.
      // Previously:
      // axios.post(process.env.REACT_APP_API_URL + DbEndpoint, filteredData),
      isProtectionReview
        ? Promise.resolve(null)
        : axios.post(process.env.REACT_APP_API_URL + DbEndpoint, filteredData),
      setFormSubmitted(true),
    ]);

    const getCallData = (report, target) => {
      return report.status === "fulfilled"
        ? has(report.value.data, target)
          ? report.value.data[target]
          : report.value.data.message
        : report.reason.message;
    };

    const getCallResponseStatusCode = (report) => {
      return has(report, "value.data.status")
        ? report.value.data.status
        : report.status === "fulfilled"
        ? 200
        : report.reason.response.status;
    };

    const report = {
      calls: {
        emailToClient: {
          status: emailToClientReport.status,
          data: getCallData(emailToClientReport, "messageId"),
          responseStatusCode: getCallResponseStatusCode(emailToClientReport),
        },
        emailToAdmin: {
          status: emailToAdminReport.status,
          data: getCallData(emailToAdminReport, "messageId"),
          responseStatusCode: getCallResponseStatusCode(emailToAdminReport),
        },
        smsToClient: {
          status: smsToClientReport.status,
          data: getCallData(smsToClientReport, "messageId"),
          responseStatusCode: getCallResponseStatusCode(smsToClientReport),
        },
        // AMENDED [10/07/2026]: Protection review resolves the DB call with a
        // plain `null` (see isProtectionReview substitution above), which isn't
        // a real axios response - getCallData/getCallResponseStatusCode assume
        // .value.data exists and throw on null. Short-circuit with a fixed
        // "not required" entry instead of calling either helper in that case.
        // Previously:
        // sendToDatabase: {
        //   status: sendToDatabaseReport.status,
        //   data: getCallData(sendToDatabaseReport, "id"),
        //   responseStatusCode: getCallResponseStatusCode(sendToDatabaseReport),
        // },
        sendToDatabase: isProtectionReview
          ? {
              status: sendToDatabaseReport.status,
              data: "No database entry required",
              responseStatusCode: null,
            }
          : {
              status: sendToDatabaseReport.status,
              data: getCallData(sendToDatabaseReport, "id"),
              responseStatusCode:
                getCallResponseStatusCode(sendToDatabaseReport),
            },
      },
      reference,
      data: emailParams,
    };

    // AMENDED [10/07/2026]: Protection review appointments have no DB entry,
    // so there's nothing to verify and no report email is sent for them.
    // Previously: this block (verification GET, then/catch, sendReport) ran
    // unconditionally for every route.
    if (!isProtectionReview) {
      axios
        .get(process.env.REACT_APP_API_URL + DbEndpoint + "/check/" + reference)
        .then((response) => {
          merge(report.calls.sendToDatabase, {
            status: sendToDatabaseReport.status,
            data: "The record has been found",
            responseStatusCode: response.status,
          });
          // AMENDED [10/07/2026]: gmail recipient name "New Enquiry" -> "Admin".
          // Previously:
          // const emailTo = [
          //   {
          //     email: "threshold.mortgages@gmail.com",
          //     name: "New Enquiry",
          //   },
          // ];
          const emailTo = [
            {
              email: "threshold.mortgages@gmail.com",
              name: "Admin",
            },
          ];
          sendReport(report, emailTo);
        })
        .catch((error) => {
          merge(report.calls.sendToDatabase, {
            status: sendToDatabaseReport.status,
            data: error.message,
            responseStatusCode: error.response.status,
          });
          // AMENDED [10/07/2026]: gmail recipient name "New Enquiry" -> "Admin"
          // (both occurrences below - dev-sooty branch and prod main-domain
          // branch). Other recipients' names (newenquiry@, Daniella Stoner,
          // Leah Newland) are unaffected - only the gmail address changes.
          // Previously:
          // const emailTo =
          //   process.env.NODE_ENV === "production"
          //     ? window.location.hostname === "contact-app-dev-sooty.vercel.app"
          //       ? [
          //           {
          //             email: "threshold.mortgages@gmail.com",
          //             name: "New Enquiry",
          //           },
          //         ]
          //       : [
          //           {
          //             email: "newenquiry@thresholdmortgages.com",
          //             name: "New Enquiry",
          //           },
          //           {
          //             email: "daniella.stoner@thresholdmortgages.com",
          //             name: "Daniella Stoner ",
          //           },
          //           {
          //             email: "leah.newland@thresholdmortgages.com",
          //             name: "Leah Newland",
          //           },
          //           {
          //             email: "threshold.mortgages@gmail.com",
          //             name: "New Enquiry",
          //           },
          //         ]
          //     : [{ email: "rich.ray@hotmail.co.uk", name: "Richard" }];
          // AMENDED [10/07/2026]: Removed the "contact-app-dev-sooty.vercel.app"
          // hostname check - that staging domain no longer exists, so the
          // condition was permanently false and always fell through to the
          // full staff list anyway. Now prod always sends to the full staff
          // list directly; dev fallback unchanged. Other recipients' names
          // (newenquiry@, Daniella Stoner, Leah Newland) are unaffected.
          // Previously:
          // const emailTo =
          //   process.env.NODE_ENV === "production"
          //     ? window.location.hostname === "contact-app-dev-sooty.vercel.app"
          //       ? [
          //           {
          //             email: "threshold.mortgages@gmail.com",
          //             name: "Admin",
          //           },
          //         ]
          //       : [
          //           {
          //             email: "newenquiry@thresholdmortgages.com",
          //             name: "New Enquiry",
          //           },
          //           {
          //             email: "daniella.stoner@thresholdmortgages.com",
          //             name: "Daniella Stoner ",
          //           },
          //           {
          //             email: "leah.newland@thresholdmortgages.com",
          //             name: "Leah Newland",
          //           },
          //           {
          //             email: "threshold.mortgages@gmail.com",
          //             name: "Admin",
          //           },
          //         ]
          //     : [{ email: "rich.ray@hotmail.co.uk", name: "Richard" }];
          const emailTo =
            process.env.NODE_ENV === "production"
              ? [
                  {
                    email: "newenquiry@thresholdmortgages.com",
                    name: "New Enquiry",
                  },
                  {
                    email: "daniella.stoner@thresholdmortgages.com",
                    name: "Daniella Stoner ",
                  },
                  {
                    email: "leah.newland@thresholdmortgages.com",
                    name: "Leah Newland",
                  },
                  {
                    email: "threshold.mortgages@gmail.com",
                    name: "Admin",
                  },
                ]
              : [{ email: "rich.ray@hotmail.co.uk", name: "Richard" }];
          sendReport(report, emailTo);
        });
    }

    const sendReport = (report, emailTo) => {
      axios.post(process.env.REACT_APP_API_URL + "/api/send-email", {
        to: emailTo,
        subject: `${reference} Lead Report`,
        templateId: 227,
        params: report,
      });
    };
  };

  return (
    <>
      <div className="mb-16">
        <div className="mb-4 flex justify-center sm:justify-start">
          <div className="w-full max-w-sm sm:max-w-full">
            {/* <h3 className="mb-2 text-xl font-bold text-black">
              Book an appointment
            </h3> */}
            <p className="mb-0 text-lg text-black">
              Select a day and time slot for a qualified adviser to call you.
            </p>
          </div>
        </div>

        <AppointmentPicker
          data={data}
          setData={setData}
          setAppointment={setAppointment}
        />
      </div>

      <div className="mb-16">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="mb-4 flex justify-center">
            <div className="w-full max-w-sm ">
              <InputField
                name="firstName"
                label="First name"
                type="text"
                help={null}
                value={data["firstName"]}
                onChange={handleChange}
                error={errors["firstName"]}
              />
            </div>
          </div>

          <div className="mb-4 flex justify-center">
            <div className="w-full max-w-sm ">
              <InputField
                name="lastName"
                label="Last name"
                type="text"
                help={null}
                value={data["lastName"]}
                onChange={handleChange}
                error={errors["lastName"]}
              />
            </div>
          </div>

          <div className="mb-4 flex justify-center">
            <div className="w-full max-w-sm ">
              <InputField
                name="email"
                label="Email"
                type="text"
                help={null}
                value={data["email"]}
                onChange={handleChange}
                error={errors["email"]}
              />
            </div>
          </div>

          <div className="mb-4 flex justify-center">
            <div className="w-full max-w-sm ">
              <InputField
                name="phone"
                label="Phone"
                type="text"
                help={null}
                value={data["phone"]}
                onChange={handleChange}
                error={errors["phone"]}
              />
            </div>
          </div>

          {/* AMENDED [2026-06-09]: Development field hidden when isNewBuild is false. */}
          {/* Previously: field rendered unconditionally (no wrapping conditional). */}
          {isNewBuild && (
            <div className="mb-0 flex justify-center">
              <div className="w-full max-w-sm ">
                <InputField
                  name="development"
                  label="Which development are you interested in?"
                  type="text"
                  help={null}
                  value={data["development"]}
                  onChange={handleChange}
                  error={errors["development"]}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mb-8 flex justify-center">
        <div className="w-full max-w-sm sm:max-w-full">
          <SingleCheckbox
            name="storeData"
            label="Tick this box if you want your details to be stored on our database, which may then be used for marketing purposes."
            help={null}
            value={data["storeData"]}
            onChange={handleCheckboxChange}
            error={errors["storeData"]}
          />
        </div>
      </div>

      <div className="mb-8 flex justify-center">
        <div className="w-full max-w-sm sm:max-w-full">
          <CheckboxGroup
            name="contactMethods"
            label={"How you would like us to contact you?"}
            options={[
              { _id: 0, option: "Email", key: "email" },
              { _id: 1, option: "Phone", key: "phone" },
              { _id: 2, option: "SMS", key: "sms" },
            ]}
            help={null}
            value={data["contactMethods"]}
            onChange={handleCheckboxGroupChange}
            error={errors["contactMethods"]}
          />
        </div>
      </div>

      <div className="mb-8 flex justify-center">
        <div className="w-full max-w-sm sm:max-w-full">
          <p className="text-sm font-medium text-black dark:text-white">
            The internet is not a secure medium and the privacy of your data
            cannot be guaranteed.
          </p>
        </div>
      </div>

      <div className="mb-0 flex justify-center">
        <div className="w-full max-w-sm sm:max-w-full">
          <ButtonDefault
            color="emerald"
            label="Submit"
            onClick={handleSubmit}
            disabled={validateForm(data, schemaObj)}
          />
        </div>
      </div>
    </>
  );
}

export default FormContact;
